import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import EvidenceAPI from "@/api/evidence/index.js";
import Loader from "@/components/Loader.js";
import { monolithMoment } from "@/utils/date-format";
import type { Evidence } from "@/Features";
import { CopyableDetailRow } from "./CopyableDetailRow";

interface EvidenceInfoCardProps {
  className?: string;
  evidenceInfo: { uuid: string };
}

export const EvidenceInfoCard = styled(
  ({ className, evidenceInfo }: EvidenceInfoCardProps) => {
    const { data, isPending, isError } = useQuery({
      queryKey: ["entity-info", "evidence", evidenceInfo.uuid],
      queryFn: () => EvidenceAPI.getEvidence({ uuid: evidenceInfo.uuid }),
    });

    if (isPending) return <Loader />;

    if (isError) {
      return (
        <div className={className}>
          <h4>Unable to load evidence details</h4>
        </div>
      );
    }

    const evidenceData = data?.[0] as Evidence | undefined;

    if (!evidenceData) {
      return (
        <div className={className}>
          <h4>Evidence not found</h4>
        </div>
      );
    }

    const titleParts: string[] = [];
    if (evidenceData.model_name) titleParts.push(evidenceData.model_name);
    if (evidenceData.item_type) titleParts.push(evidenceData.item_type);

    return (
      <div className={className}>
        <h4>{titleParts.join(" | ") || "Evidence"}</h4>
        <CopyableDetailRow
          label="Evidence Number"
          value={evidenceData.evidence_number}
        />
        <CopyableDetailRow label="Item Name" value={evidenceData.model_name} />
        <CopyableDetailRow
          label="Case Number"
          value={evidenceData.case_number}
        />
        <CopyableDetailRow
          label="Make/Provider"
          value={evidenceData.manufacturer}
        />
        <CopyableDetailRow
          label="Created"
          value={
            evidenceData.created_on
              ? monolithMoment({
                  timestamp: evidenceData.created_on,
                  includeTime: true,
                  includeZone: true,
                })
              : null
          }
        />
        <CopyableDetailRow label="Progress" value={evidenceData.progress} />
        <CopyableDetailRow
          label="Description"
          value={evidenceData.description}
        />
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: ${({ theme }) => theme.palette.text.primary};

  h4 {
    margin: 0 0 4px 0;
    font-size: 0.75rem;
  }
`;

export default EvidenceInfoCard;
